"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { RecaptchaNotice } from "@/components/shared/RecaptchaNotice";
import { formatContactEmail } from "@/lib/format-contact-email";
import { parseNotifyEmailListFromEnvChain } from "@/lib/notify-email-list";
import { useRecaptchaToken } from "@/lib/recaptcha-client";

const CONTACT_NOTIFY_TO = parseNotifyEmailListFromEnvChain(
  process.env.NEXT_PUBLIC_CONTACT_NOTIFY_EMAIL,
  process.env.NEXT_PUBLIC_DIGITAL_REPORT_NOTIFY_EMAIL,
);

const contactSchema = z.object({
  firstName: z.string().trim().min(1, { message: "Enter your first name" }),
  lastName: z.string().trim().min(1, { message: "Enter your last name" }),
  email: z.string().trim().email({ message: "Enter a valid email address" }),
  message: z.string().trim().min(1, { message: "Enter your message" }),
  updates: z.boolean(),
});

type ContactValues = z.infer<typeof contactSchema>;

export type ContactFormPanelProps = {
  className?: string;
};

export function ContactFormPanel({ className }: ContactFormPanelProps) {
  const { getToken } = useRecaptchaToken();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const { reset, ...form } = useForm<ContactValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      message: "",
      updates: true,
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);

    if (CONTACT_NOTIFY_TO.length === 0) {
      setSubmitError(
        "Notification email is not configured. Add NEXT_PUBLIC_CONTACT_NOTIFY_EMAIL to your environment.",
      );
      return;
    }

    const html = formatContactEmail(
      {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        message: values.message,
        wantsUpdates: values.updates ? "Yes" : "No",
      },
      { title: "Contact form submission" },
    );

    const recaptcha = await getToken("contact_form_submit");
    if (!recaptcha.ok) {
      setSubmitError(recaptcha.error);
      return;
    }

    const res = await fetch("/api/contact", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: `${values.firstName} ${values.lastName}`.trim(),
        email: values.email,
        phone: undefined,
        marketingConsent: values.updates,
        recaptchaToken: recaptcha.token,
        recaptchaAction: "contact_form_submit",
        notification: {
          to: CONTACT_NOTIFY_TO.length === 1 ? CONTACT_NOTIFY_TO[0]! : CONTACT_NOTIFY_TO,
          subject: `Contact form: ${values.firstName} ${values.lastName}`.trim(),
          html,
        },
      }),
    });
    const data = (await res.json()) as {
      ok?: boolean;
      email?: { ok?: boolean };
      success?: boolean;
      error?: string;
    };

    if (res.ok && data.ok === true && data.email?.ok) {
      setSubmitSuccess(true);
      reset();
      return;
    }

    setSubmitError(
      typeof data.error === "string"
        ? data.error
        : data.success === false
          ? "Could not save your contact details. Please try again."
          : "Could not submit. Please try again.",
    );
  });

  return (
    <div className={className}>
      {submitSuccess ? (
        <p
          className="mt-8 rounded-lg border border-green-700/25 bg-green-50 px-4 py-3 text-sm text-green-800"
          role="status"
        >
          Thanks, we received your message and will get back to you shortly.
        </p>
      ) : (
        <form className="mt-4 space-y-4 md:mt-8 md:space-y-6" onSubmit={onSubmit} noValidate>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <input
              aria-label="First name"
              placeholder="First name *"
              className="w-full border-0 border-b border-[#22242a]/20 bg-transparent px-0 py-1.5 text-base outline-none md:py-2"
              {...form.register("firstName")}
            />
            <input
              aria-label="Last name"
              placeholder="Last name *"
              className="w-full border-0 border-b border-[#22242a]/20 bg-transparent px-0 py-1.5 text-base outline-none md:py-2"
              {...form.register("lastName")}
            />
          </div>
          {form.formState.errors.firstName || form.formState.errors.lastName ? (
            <p className="text-xs text-red-600" role="alert">
              {form.formState.errors.firstName?.message ?? form.formState.errors.lastName?.message}
            </p>
          ) : null}

          <input
            aria-label="Email"
            placeholder="E-mail *"
            className="w-full border-0 border-b border-[#22242a]/20 bg-transparent px-0 py-1.5 text-base outline-none md:py-2"
            {...form.register("email")}
          />
          {form.formState.errors.email ? (
            <p className="text-xs text-red-600" role="alert">
              {form.formState.errors.email.message}
            </p>
          ) : null}

          <textarea
            aria-label="Your message"
            placeholder="Your message"
            rows={2}
            className="w-full resize-none border-0 border-b border-[#22242a]/20 bg-transparent px-0 py-1.5 text-base outline-none md:py-2"
            {...form.register("message")}
          />
          {form.formState.errors.message ? (
            <p className="text-xs text-red-600" role="alert">
              {form.formState.errors.message.message}
            </p>
          ) : null}

          <label className="flex items-center gap-3 text-xs text-[#22242a]/80 md:text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 border border-[#22242a]/30 accent-[#ff5c00]"
              {...form.register("updates")}
            />
            Yes please keep me up to date with everything digital
          </label>

          {submitError ? (
            <p
              className="rounded-lg border border-red-700/25 bg-red-50 px-4 py-3 text-sm text-red-700"
              role="alert"
            >
              {submitError}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={form.formState.isSubmitting}
            className="inline-flex min-h-11 w-full items-center justify-center rounded-full bg-[#ff5c00] px-8 text-base font-medium text-white transition-colors hover:bg-[#ea4f10] disabled:opacity-60 md:min-h-12 md:w-auto"
          >
            {form.formState.isSubmitting ? "Sending..." : "Submit message"}
          </button>
          <RecaptchaNotice
            className="text-xs text-[#22242a]/70"
            linkClassName="underline underline-offset-2 hover:text-[#22242a]"
          />
        </form>
      )}
    </div>
  );
}
